import React, { useState, useEffect, useRef, useContext } from "react";
import {
    makeStyles,
    TextField,
    IconButton,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    Avatar,
    ClickAwayListener,
    CircularProgress,
    Badge,
    InputAdornment,
    Button
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import InsertEmoticonIcon from "@material-ui/icons/InsertEmoticon";
import MicIcon from "@material-ui/icons/Mic";
import StopIcon from "@material-ui/icons/Stop";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";

import { Picker } from "emoji-mart";
import "emoji-mart/css/emoji-mart.css";

import api from "../../services/api";
import socketConnection from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import RecordingTimer from "../../components/MessageInput/RecordingTimer";
import Audio from "../../components/Audio";
import {
    normalizeFilesForUpload,
    normalizeFileForUpload,
} from "../../utils/upload";

const AUDIO_CONSTRAINTS = {
    audio: {
        sampleRate: { ideal: 48000 },
        sampleSize: { ideal: 16 },
        channelCount: { ideal: 1 },
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
    },
};

const RECORDING_AUDIO_BITS_PER_SECOND = 128000;

const mergeMessagesById = (current, incoming, excludedIds = new Set()) => {
    const byId = new Map(current.map((message) => [message.id, message]));
    incoming.forEach((message) => {
        if (!message?.id || excludedIds.has(message.id)) return;
        const existing = byId.get(message.id);
        if (!existing) {
            byId.set(message.id, message);
            return;
        }
        const existingTime = new Date(existing.updatedAt || existing.createdAt || 0).getTime();
        const incomingTime = new Date(message.updatedAt || message.createdAt || 0).getTime();
        byId.set(message.id, incomingTime >= existingTime
            ? { ...existing, ...message }
            : { ...message, ...existing });
    });
    return [...byId.values()].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
};

const getBestRecordingMimeType = () => {
    if (
        typeof MediaRecorder === "undefined" ||
        typeof MediaRecorder.isTypeSupported !== "function"
    ) {
        return "";
    }
    if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) {
        return "audio/ogg;codecs=opus";
    }
    if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        return "audio/webm;codecs=opus";
    }
    return "";
};

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        height: "calc(100vh - 68px)",
        backgroundColor: theme.palette.background.default,
        overflow: "hidden",
        fontFamily: "Roboto, sans-serif",
        minHeight: 0,
    },
    sidebar: {
        width: 320,
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        borderRight: `1px solid ${theme.palette.divider}`,
        minHeight: 0,
        flexShrink: 0,
    },
    sidebarHeader: {
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
    },
    queueSearch: {
        margin: theme.spacing(1, 1, 0.5),
    },
    queueItem: {
        margin: theme.spacing(0, 0.75, 0.5),
        width: "auto",
        borderRadius: 10,
    },
    selectedQueueItem: {
        backgroundColor: `${theme.palette.primary.main}14`,
        "&:hover": { backgroundColor: `${theme.palette.primary.main}20` },
    },
    chatContainer: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.default,
        position: "relative",
        minHeight: 0,
    },
    chatHeader: {
        minHeight: 64,
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        zIndex: 1,
    },
    chatHeaderInfo: { minWidth: 0, flex: 1 },
    headerActions: { display: "flex", alignItems: "center" },
    messageList: {
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        padding: "20px clamp(12px, 3vw, 36px)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        backgroundImage: "radial-gradient(rgba(0,0,0,0.025) 1px, transparent 1px)",
        backgroundSize: "18px 18px",
    },
    dateDivider: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "20px 0",
        "& span": {
            backgroundColor: "#fff",
            color: "#54656f",
            padding: "5px 12px",
            borderRadius: "8px",
            fontSize: "12px",
            fontWeight: "bold",
            textTransform: "uppercase",
            boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)"
        }
    },
    messageBubble: {
        maxWidth: "min(76%, 640px)",
        padding: "10px 12px 22px",
        borderRadius: 12,
        marginBottom: 6,
        boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
        fontSize: "14px",
        lineHeight: 1.5,
        wordBreak: "break-word",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minWidth: 120,
    },
    fromMe: {
        alignSelf: "flex-end",
        backgroundColor: theme.palette.type === "dark" ? "#174d42" : "#e5f5ec",
        borderTopRightRadius: 4,
    },
    fromOthers: {
        alignSelf: "flex-start",
        backgroundColor: "#ffffff",
        borderTopLeftRadius: 4,
    },
    messageTimestamp: {
        fontSize: "10px",
        color: "rgba(0, 0, 0, 0.45)",
        position: "absolute",
        right: 8,
        bottom: 4,
        lineHeight: 1,
    },
    inputArea: {
        padding: theme.spacing(1, 1.5),
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.5),
        position: "relative",
        zIndex: 2,
    },
    emojiBox: { position: "absolute", bottom: "63px", left: "10px", zIndex: 999 },
    userName: { fontSize: "11px", fontWeight: "bold", marginBottom: 2 },
    hiddenInput: { display: "none" },
    audioPlayer: {
        marginTop: 4,
        width: "100%",
        minWidth: 250,
        maxWidth: 320,
        borderRadius: 8,
        display: "block",
    },
    mediaMessageTimestamp: {
        position: "static",
        alignSelf: "flex-end",
        marginTop: 4,
    },
    mediaImage: {
        marginTop: 4,
        width: "100%",
        maxWidth: 300,
        borderRadius: 8,
        display: "block",
        objectFit: "cover",
        cursor: "pointer",
    },
    mediaDocument: {
        marginTop: 4,
        display: "flex",
        alignItems: "center",
        backgroundColor: "rgba(0,0,0,0.05)",
        padding: "8px 10px",
        borderRadius: 8,
        border: "1px solid rgba(0,0,0,0.08)",
        maxWidth: 300,
    },
    mediaDocumentText: {
        flex: 1,
        marginRight: 8,
        fontWeight: 500,
    },
    quotedContainer: { backgroundColor: "rgba(0,0,0,0.06)", borderLeft: "4px solid #075e54", padding: "5px 8px", marginBottom: 4, borderRadius: 4, cursor: "pointer" },
    messageText: {
        whiteSpace: "pre-wrap",
        paddingRight: 52,
    },
    deleteButton: {
        position: "absolute",
        top: 2,
        right: 2,
        opacity: 0.65,
        "&:hover": {
            opacity: 1,
        },
    },
    recordingTimerWrapper: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#fff",
        borderRadius: 20,
        padding: "0 10px",
        marginRight: 8,
        height: 40,
        minWidth: 84,
        "& span": {
            fontWeight: 600,
            fontSize: 14,
        },
    },
    replyPreviewContainer: { background: theme.palette.background.paper, padding: "8px 12px", borderLeft: `4px solid ${theme.palette.primary.main}`, display: "flex", alignItems: "center" },
    rootWithSelectedQueue: {
        [theme.breakpoints.down("sm")]: {
            "& $sidebar": { display: "none" },
        },
    },
    emptyState: {
        margin: "auto",
        padding: theme.spacing(3),
        textAlign: "center",
        color: theme.palette.text.secondary,
    },
    olderMessagesButton: {
        alignSelf: "center",
        marginBottom: theme.spacing(1),
        textTransform: "none",
    },
    mobileOnly: {
        display: "none",
        [theme.breakpoints.down("sm")]: { display: "inline-flex" },
    },
    desktopOnly: {
        [theme.breakpoints.down("sm")]: { display: "none" },
    },
    [theme.breakpoints.down("sm")]: {
        root: { height: "calc(100vh - 56px)" },
        sidebar: { width: "100%" },
        messageBubble: { maxWidth: "90%", minWidth: 0 },
        audioPlayer: { minWidth: 0, maxWidth: "min(72vw, 320px)" },
    },
}));

const InternalChat = () => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);

    const [queues, setQueues] = useState([]);
    const [selectedQueue, setSelectedQueue] = useState(null);
    const [messages, setMessages] = useState([]);
    const [content, setContent] = useState("");
    const [showEmoji, setShowEmoji] = useState(false);
    const [loadingMedia, setLoadingMedia] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [replyingMessage, setReplyingMessage] = useState(null);
    const [unreadMessages, setUnreadMessages] = useState({});
    const [queueSearch, setQueueSearch] = useState("");
    const [loadingQueues, setLoadingQueues] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [historyCursor, setHistoryCursor] = useState(null);
    const [sending, setSending] = useState(false);

    const scrollRef = useRef();
    const fileInputRef = useRef();
    const recorderRef = useRef(null);
    const recordingStreamRef = useRef(null);
    const recordingChunksRef = useRef([]);
    const previousMessagesLengthRef = useRef(0);
    const socketRef = useRef(null);
    const selectedQueueRef = useRef(null);
    const fetchRequestRef = useRef(0);
    const deletedMessageIdsRef = useRef(new Set());
    const olderScrollHeightRef = useRef(null);

    const upsertMessage = (incomingMessage) => {
        if (!incomingMessage?.id) return;
        setMessages((prev) => mergeMessagesById(prev, [incomingMessage], deletedMessageIdsRef.current));
    };

    // Helpers de Formatação e Agrupamento
    const getUserColor = (userId) => {
        if (!userId) return "#757575";
        const hash = String(userId).split("").reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0);
        return `hsl(${Math.abs(hash) % 360}, 70%, 40%)`;
    };

    const formatMessageTime = (dateString) => {
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(date);
        } catch (e) { return ""; }
    };

    const groupMessagesByDate = (messages) => {
        const groups = {};
        messages.forEach((msg) => {
            const date = new Date(msg.createdAt).toLocaleDateString('pt-BR');
            if (!groups[date]) groups[date] = [];
            groups[date].push(msg);
        });
        return groups;
    };

    const getLabelDate = (dateStr) => {
        const today = new Date().toLocaleDateString('pt-BR');
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toLocaleDateString('pt-BR');

        if (dateStr === today) return "Hoje";
        if (dateStr === yesterdayStr) return "Ontem";
        return dateStr;
    };

    useEffect(() => {
        const fetchQueues = async () => {
            try {
                const { data } = await api.get(`/users/${user.id}`);
                setQueues(Array.isArray(data.queues) ? data.queues : []);
            } catch (err) { toast.error("Não foi possível carregar seus setores."); }
            finally { setLoadingQueues(false); }
        };
        fetchQueues();
    }, [user.id]);

    useEffect(() => {
        selectedQueueRef.current = selectedQueue;
    }, [selectedQueue]);

    const fetchMessages = async (queueId, cursor = null) => {
        if (!queueId) return;
        const requestId = ++fetchRequestRef.current;
        if (cursor) {
            setLoadingOlderMessages(true);
            olderScrollHeightRef.current = scrollRef.current?.scrollHeight || null;
        } else {
            olderScrollHeightRef.current = null;
            setLoadingMessages(true);
        }
        try {
            const { data } = await api.get(`/internal-messages/${queueId}`, {
                params: cursor || undefined,
            });
            if (requestId !== fetchRequestRef.current) return;
            if (Number(selectedQueueRef.current?.id) !== Number(queueId)) return;
            const pageMessages = Array.isArray(data) ? data : data.messages;
            setMessages((current) => mergeMessagesById(
                current,
                Array.isArray(pageMessages) ? pageMessages : [],
                deletedMessageIdsRef.current,
            ));
            setHasMoreMessages(Boolean(data.hasMore));
            if (cursor) {
                setHistoryCursor(data.nextCursor || null);
            } else {
                setHistoryCursor((current) => current || data.nextCursor || null);
            }
        } catch (err) {
            if (requestId === fetchRequestRef.current) {
                toast.error("Não foi possível carregar as mensagens. Tente atualizar.");
            }
        } finally {
            if (requestId === fetchRequestRef.current) {
                if (cursor) setLoadingOlderMessages(false);
                else setLoadingMessages(false);
            }
        }
    };

    useEffect(() => {
        const socket = socketConnection();
        socketRef.current = socket;

        const joinQueueRooms = () => {
            queues.forEach((q) => socket.emit("joinInternalChatQueue", q.id, (result) => {
                if (result?.ok && Number(selectedQueueRef.current?.id) === Number(q.id)) {
                    fetchMessages(q.id);
                }
            }));
        };

        socket.on("connect", joinQueueRooms);
        joinQueueRooms();

        socket.on("appMessage", (data) => {
            if (data.action === "create") {
                const msgQueueId = Number(data.message?.queueId);
                const currentSelectedQueueId = Number(selectedQueueRef.current?.id);

                if (currentSelectedQueueId && msgQueueId === currentSelectedQueueId) {
                    upsertMessage(data.message);
                } else if (msgQueueId) {
                    setUnreadMessages((prev) => ({ ...prev, [msgQueueId]: (prev[msgQueueId] || 0) + 1 }));
                }
            }

            if (data.action === "delete") {
                const msgQueueId = Number(data.queueId);
                const currentSelectedQueueId = Number(selectedQueueRef.current?.id);
                if (currentSelectedQueueId && msgQueueId === currentSelectedQueueId) {
                    deletedMessageIdsRef.current.add(data.messageId);
                    setMessages((prev) => prev.filter((msg) => msg.id !== data.messageId));
                }
            }
        });

        return () => {
            queues.forEach((q) => socket.emit("leaveInternalChatQueue", q.id));
            socket.off("connect", joinQueueRooms);
            socket.off("appMessage");
            socket.disconnect();
            socketRef.current = null;
        };
    }, [queues]);

    useEffect(() => {
        if (!selectedQueue) return;
        setMessages([]);
        fetchMessages(selectedQueue.id);
    }, [selectedQueue]);

    useEffect(() => {
        return () => {
            if (recorderRef.current && recorderRef.current.state !== "inactive") {
                recorderRef.current.stop();
            }
            if (recordingStreamRef.current) {
                recordingStreamRef.current.getTracks().forEach((track) => track.stop());
                recordingStreamRef.current = null;
            }
        };
    }, []);

    const isNearBottom = () => {
        if (!scrollRef.current) return true;
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        return scrollHeight - (scrollTop + clientHeight) < 120;
    };

    const scrollToBottom = (behavior = "auto") => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior,
        });
    };

    useEffect(() => {
        if (!scrollRef.current) return;

        if (olderScrollHeightRef.current !== null) {
            const heightDifference = scrollRef.current.scrollHeight - olderScrollHeightRef.current;
            scrollRef.current.scrollTop += heightDifference;
            olderScrollHeightRef.current = null;
            previousMessagesLengthRef.current = messages.length;
            return;
        }

        const isFirstLoad = previousMessagesLengthRef.current === 0;
        const hasNewMessage = messages.length > previousMessagesLengthRef.current;
        const shouldAutoScroll = isFirstLoad || isNearBottom();

        if (hasNewMessage && shouldAutoScroll) {
            scrollToBottom(isFirstLoad ? "auto" : "smooth");
        }

        previousMessagesLengthRef.current = messages.length;
    }, [messages]);

    const handleSelectQueue = (queue) => {
        selectedQueueRef.current = queue;
        fetchRequestRef.current += 1;
        deletedMessageIdsRef.current = new Set();
        setHistoryCursor(null);
        setHasMoreMessages(false);
        setLoadingMessages(false);
        setLoadingOlderMessages(false);
        olderScrollHeightRef.current = null;
        setMessages([]);
        setSelectedQueue(queue);
        setUnreadMessages((prev) => ({ ...prev, [queue.id]: 0 }));
        previousMessagesLengthRef.current = 0;
    };

    const handleAddEmoji = (e) => { setContent(prev => prev + e.native); };

    const handleFileUpload = async (e) => {
        const files = e.target.files;
        if (!files || !selectedQueue) return;
        setLoadingMedia(true);
        try {
            const normalizedFiles = await normalizeFilesForUpload(Array.from(files));
            const formData = new FormData();
            formData.append("queueId", selectedQueue.id);
            if (replyingMessage) formData.append("quotedMsgId", replyingMessage.id);
            normalizedFiles.forEach(file => formData.append("medias", file));
            await api.post("/internal-messages/media", formData);
            await fetchMessages(selectedQueue.id);
            setReplyingMessage(null);
        } catch (err) { toast.error(err?.message || "Erro ao enviar arquivo"); }
        setLoadingMedia(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const handleStartRecording = async () => {
        if (!selectedQueue || loadingMedia) return;
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
            toast.error("Gravação de áudio não suportada neste navegador.");
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS);
            recordingStreamRef.current = stream;
            const mimeType = getBestRecordingMimeType();
            const recorderOptions = {
                audioBitsPerSecond: RECORDING_AUDIO_BITS_PER_SECOND,
            };

            if (mimeType) {
                recorderOptions.mimeType = mimeType;
            }

            recorderRef.current = new MediaRecorder(stream, recorderOptions);
            recordingChunksRef.current = [];
            recorderRef.current.ondataavailable = (e) => {
                if (e.data?.size > 0) {
                    recordingChunksRef.current.push(e.data);
                }
            };
            recorderRef.current.onstop = async () => {
                const blobType = mimeType || "audio/webm";
                const blob = new Blob(recordingChunksRef.current, { type: blobType });
                recordingChunksRef.current = [];
                setLoadingMedia(true);
                try {
                    const extension = blobType.includes("ogg") ? "ogg" : "webm";
                    const audioFile = await normalizeFileForUpload(
                        new File([blob], `audio_interno_${Date.now()}.${extension}`, { type: blobType })
                    );
                    const formData = new FormData();
                    formData.append("queueId", selectedQueue.id);
                    formData.append("medias", audioFile);
                    if (replyingMessage) formData.append("quotedMsgId", replyingMessage.id);
                    await api.post("/internal-messages/media", formData);
                    await fetchMessages(selectedQueue.id);
                    setReplyingMessage(null);
                } catch (err) { toast.error("Erro ao enviar áudio"); }
                finally {
                    setLoadingMedia(false);
                    setIsRecording(false);
                    if (recordingStreamRef.current) {
                        recordingStreamRef.current.getTracks().forEach(track => track.stop());
                        recordingStreamRef.current = null;
                    }
                    recorderRef.current = null;
                }
            };
            recorderRef.current.start(250);
            setIsRecording(true);
        } catch (err) {
            toast.error("Falha no microfone.");
            setIsRecording(false);
        }
    };

    const handleSend = async () => {
        if (!content.trim() || !selectedQueue || sending) return;
        setSending(true);
        try {
            const { data } = await api.post("/internal-messages", {
                body: content,
                queueId: selectedQueue.id,
                quotedMsgId: replyingMessage?.id
            });
            if (data?.id) {
                upsertMessage(data);
            } else {
                await fetchMessages(selectedQueue.id);
            }
            setContent("");
            setReplyingMessage(null);
            setShowEmoji(false);
        } catch (err) { toast.error("Não foi possível enviar a mensagem."); }
        finally { setSending(false); }
    };

    const handleDeleteMessage = async (messageId) => {
        try {
            await api.delete(`/internal-messages/${messageId}`);
            deletedMessageIdsRef.current.add(messageId);
            setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
        } catch (err) {
            toast.error("Erro ao excluir mensagem");
        }
    };

    const renderMessageMedia = (msg) => {
        const mediaUrl = msg.mediaUrl || "";
        const isImage = msg.mediaType === "image" || /\.(jpg|jpeg|png|gif|webp)$/i.test(mediaUrl);

        if (msg.mediaType === "audio") {
            return <Audio url={mediaUrl} />;
        }

        if (isImage) {
            return (
                <img
                    src={mediaUrl}
                    alt="mídia"
                    className={classes.mediaImage}
                    onClick={() => window.open(mediaUrl, "_blank")}
                />
            );
        }

        return (
            <div className={classes.mediaDocument}>
                <Typography variant="caption" noWrap className={classes.mediaDocumentText}>
                    {msg.body || "Arquivo"}
                </Typography>
                <IconButton size="small" color="primary" component="a" href={mediaUrl} download target="_blank" rel="noreferrer">
                    <GetAppIcon fontSize="small" />
                </IconButton>
            </div>
        );
    };

    return (
        <div className={`${classes.root} ${selectedQueue ? classes.rootWithSelectedQueue : ""}`}>
            <div className={classes.sidebar}>
                <div className={classes.sidebarHeader}>
                    <Typography variant="h6" style={{ fontWeight: 700 }}>Comunicação interna</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {queues.length} {queues.length === 1 ? "setor disponível" : "setores disponíveis"}
                    </Typography>
                </div>
                <TextField
                    className={classes.queueSearch}
                    size="small"
                    variant="outlined"
                    value={queueSearch}
                    onChange={(event) => setQueueSearch(event.target.value)}
                    placeholder="Buscar setor"
                    inputProps={{ "aria-label": "Buscar setor" }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                />
                <Divider />
                <List style={{ overflowY: "auto" }}>
                    {loadingQueues && <div className={classes.emptyState}><CircularProgress size={22} /></div>}
                    {queues.filter((queue) => queue.name.toLowerCase().includes(queueSearch.trim().toLowerCase())).map((q) => (
                        <ListItem
                            button
                            key={q.id}
                            selected={selectedQueue?.id === q.id}
                            className={`${classes.queueItem} ${selectedQueue?.id === q.id ? classes.selectedQueueItem : ""}`}
                            onClick={() => handleSelectQueue(q)}
                        >
                            <Badge badgeContent={unreadMessages[q.id]} color="secondary" overlap="circular">
                                <Avatar style={{ backgroundColor: q.color || "#ccc", marginRight: 12 }}>{q.name.charAt(0).toUpperCase()}</Avatar>
                            </Badge>
                            <ListItemText
                                primary={q.name}
                                secondary={q.description}
                                primaryTypographyProps={{ style: { fontWeight: unreadMessages[q.id] > 0 ? 'bold' : 'normal' } }}
                            />
                        </ListItem>
                    ))}
                    {!loadingQueues && queues.length === 0 && (
                        <div className={classes.emptyState}>Você ainda não está vinculado a nenhum setor.</div>
                    )}
                    {!loadingQueues && queues.length > 0 && !queues.some((queue) => queue.name.toLowerCase().includes(queueSearch.trim().toLowerCase())) && (
                        <div className={classes.emptyState}>Nenhum setor encontrado.</div>
                    )}
                </List>
            </div>

            <div className={classes.chatContainer}>
                {selectedQueue ? (
                    <>
                        <div className={classes.chatHeader}>
                            <IconButton className={classes.mobileOnly} aria-label="Voltar para os setores" onClick={() => { selectedQueueRef.current = null; setSelectedQueue(null); }}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Avatar style={{ backgroundColor: selectedQueue.color || "#607d8b" }}>
                                {selectedQueue.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <div className={classes.chatHeaderInfo}>
                                <Typography variant="subtitle1" noWrap style={{ fontWeight: 700 }}>{selectedQueue.name}</Typography>
                                {selectedQueue.description && <Typography variant="caption" color="textSecondary" noWrap>{selectedQueue.description}</Typography>}
                            </div>
                            <div className={classes.headerActions}>
                                <IconButton aria-label="Atualizar mensagens" title="Atualizar mensagens" onClick={() => fetchMessages(selectedQueue.id)} disabled={loadingMessages}>
                                    {loadingMessages ? <CircularProgress size={20} /> : <RefreshIcon />}
                                </IconButton>
                                <Typography className={classes.desktopOnly} variant="caption" color="textSecondary">{messages.length} mensagens</Typography>
                            </div>
                        </div>

                        <div className={classes.messageList} ref={scrollRef}>
                            {hasMoreMessages && (
                                <Button
                                    className={classes.olderMessagesButton}
                                    size="small"
                                    onClick={() => fetchMessages(selectedQueue.id, historyCursor)}
                                    disabled={loadingOlderMessages || !historyCursor}
                                    startIcon={loadingOlderMessages ? <CircularProgress size={16} /> : null}
                                >
                                    {loadingOlderMessages ? "Carregando..." : "Carregar mensagens anteriores"}
                                </Button>
                            )}
                            {loadingMessages && messages.length === 0 ? (
                                <div className={classes.emptyState}><CircularProgress size={26} /></div>
                            ) : messages.length === 0 ? (
                                <div className={classes.emptyState}>
                                    <Typography variant="h6">Comece a conversa</Typography>
                                    <Typography variant="body2">As mensagens deste setor aparecerão aqui.</Typography>
                                </div>
                            ) : Object.entries(groupMessagesByDate(messages)).map(([date, msgs]) => (
                                <React.Fragment key={date}>
                                    <div className={classes.dateDivider}>
                                        <span>{getLabelDate(date)}</span>
                                    </div>
                                    {msgs.map((msg, i) => {
                                        const isMe = Number(msg.userId) === Number(user?.id);
                                        const userColor = getUserColor(msg.userId);

                                        return (
                                            <div key={msg.id || i}
                                                className={`${classes.messageBubble} ${isMe ? classes.fromMe : classes.fromOthers}`}
                                                style={!isMe ? { borderLeft: `3px solid ${userColor}` } : {}}
                                                onDoubleClick={() => setReplyingMessage(msg)}
                                            >
                                                {isMe && (
                                                    <IconButton
                                                        size="small"
                                                        className={classes.deleteButton}
                                                        onClick={() => handleDeleteMessage(msg.id)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                {!isMe && (
                                                    <div className={classes.userName} style={{ color: userColor }}>
                                                        {msg.user?.name || "Colega"}
                                                    </div>
                                                )}
                                                {msg.quotedMsg && (
                                                    <div className={classes.quotedContainer}>
                                                        <Typography variant="caption" style={{ fontWeight: "bold", color: "#075e54" }}>
                                                            {msg.quotedMsg.user?.name}
                                                        </Typography>
                                                        <Typography variant="caption" display="block" noWrap>
                                                            {msg.quotedMsg.body}
                                                        </Typography>
                                                    </div>
                                                )}
                                                {msg.mediaUrl ? (
                                                    renderMessageMedia(msg)
                                                ) : (
                                                    <Typography variant="body2" className={classes.messageText}>{msg.body}</Typography>
                                                )}
                                                <span className={`${classes.messageTimestamp} ${msg.mediaUrl ? classes.mediaMessageTimestamp : ""}`}>
                                                    {formatMessageTime(msg.createdAt)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </div>

                        {replyingMessage && (
                            <div className={classes.replyPreviewContainer}>
                                <div style={{ flex: 1 }}>
                                    <Typography variant="caption" style={{ color: "#075e54", fontWeight: "bold" }}>Respondendo a {replyingMessage.user?.name}</Typography>
                                    <Typography variant="body2" noWrap>{replyingMessage.body}</Typography>
                                </div>
                                <IconButton size="small" onClick={() => setReplyingMessage(null)}><CloseIcon fontSize="small" /></IconButton>
                            </div>
                        )}

                        <div className={classes.inputArea}>
                            <IconButton aria-label="Inserir emoji" onClick={() => setShowEmoji(!showEmoji)}><InsertEmoticonIcon /></IconButton>
                            <input type="file" multiple className={classes.hiddenInput} ref={fileInputRef} onChange={handleFileUpload} />
                            <IconButton aria-label="Anexar arquivo" onClick={() => fileInputRef.current?.click()} disabled={loadingMedia || isRecording}>
                                {loadingMedia ? <CircularProgress size={24} /> : <AttachFileIcon />}
                            </IconButton>
                            {isRecording && (
                                <div className={classes.recordingTimerWrapper}>
                                    <RecordingTimer />
                                </div>
                            )}
                            <TextField
                                fullWidth variant="outlined" size="small" value={content}
                                placeholder={isRecording ? "Gravando..." : "Escreva sua mensagem..."}
                                disabled={isRecording}
                                onChange={(e) => setContent(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                                inputProps={{ "aria-label": "Escreva uma mensagem para o setor" }}
                                multiline
                                maxRows={5}
                                style={{ backgroundColor: "#fff", borderRadius: 20 }}
                            />
                            {content.trim() ? (
                                <IconButton aria-label="Enviar mensagem" color="primary" onClick={handleSend} disabled={sending || loadingMedia}>
                                    {sending ? <CircularProgress size={20} /> : <SendIcon />}
                                </IconButton>
                            ) : (
                                <IconButton
                                    color={isRecording ? "secondary" : "primary"}
                                    onClick={isRecording ? () => recorderRef.current?.stop() : handleStartRecording}
                                >
                                    {isRecording ? <StopIcon /> : <MicIcon />}
                                </IconButton>
                            )}
                        </div>
                        {showEmoji && (
                            <div className={classes.emojiBox}>
                                <ClickAwayListener onClickAway={() => setShowEmoji(false)}>
                                    <Picker perLine={8} showPreview={false} onSelect={handleAddEmoji} set="apple" />
                                </ClickAwayListener>
                            </div>
                        )}
                    </>
                ) : (
                    <div style={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
                        <Typography variant="h5" style={{ opacity: 0.5 }}>Selecione um canal para conversar!</Typography>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InternalChat;
